import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { agentApi } from '../services/agentApi';
import { AgentCreate, AgentUpdate, AgentScaffoldRequest, ListAgentsParams, ListChatsParams } from '../types';

export const AI_AGENTS_QUERY_KEY = 'ai-agents';
export const AVATARS_QUERY_KEY = 'available-avatars';
export const AGENT_CHATS_QUERY_KEY = 'agent-chats';
export const AGENT_STATUS_QUERY_KEY = 'agent-status';
export const AGENT_PROMPT_CONFIG_QUERY_KEY = 'agent-prompt-config';

export function useAgents(params?: ListAgentsParams) {
  return useQuery({
    queryKey: [AI_AGENTS_QUERY_KEY, params],
    queryFn: () => agentApi.listAgents(params),
    placeholderData: keepPreviousData,
  });
}

export function useAgent(id: string, includeFiles?: boolean) {
  return useQuery({
    queryKey: [AI_AGENTS_QUERY_KEY, id, { includeFiles }],
    queryFn: () => agentApi.getAgent(id, includeFiles),
    enabled: !!id,
  });
}

export function useCreateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AgentCreate) => agentApi.createAgent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AI_AGENTS_QUERY_KEY] });
    },
  });
}

// Prompt-to-scaffold generation. Not a persistence mutation — it returns a draft
// for review — but modeled as a mutation for the pending/error UX.
export function useScaffoldAgent() {
  return useMutation({
    mutationFn: (data: AgentScaffoldRequest) => agentApi.scaffoldAgent(data),
  });
}

export function useUpdateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AgentUpdate }) =>
      agentApi.updateAgent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AI_AGENTS_QUERY_KEY] });
    },
  });
}

export function useDeleteAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentApi.deleteAgent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AI_AGENTS_QUERY_KEY] });
    },
  });
}

export function useReorderAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, position }: { id: string; position: number }) =>
      agentApi.reorderAgent(id, position),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AI_AGENTS_QUERY_KEY] });
    },
  });
}

export function useFileSystemStats(agentId: string) {
  return useQuery({
    queryKey: [AI_AGENTS_QUERY_KEY, agentId, 'file-system-stats'],
    queryFn: () => agentApi.getFileSystemStats(agentId),
    enabled: !!agentId,
  });
}

export function useContextFiles(agentId: string) {
  return useQuery({
    queryKey: [AI_AGENTS_QUERY_KEY, agentId, 'context-files'],
    queryFn: () => agentApi.listContextFiles(agentId),
    enabled: !!agentId,
  });
}

export function useAddContextFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, fileId }: { agentId: string; fileId: string }) =>
      agentApi.addContextFile(agentId, fileId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [AI_AGENTS_QUERY_KEY, variables.agentId, 'context-files'] });
      queryClient.invalidateQueries({ queryKey: [AI_AGENTS_QUERY_KEY, variables.agentId] });
    },
  });
}

export function useAddContextFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, folderId }: { agentId: string; folderId: string }) =>
      agentApi.addContextFolder(agentId, folderId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [AI_AGENTS_QUERY_KEY, variables.agentId, 'context-files'] });
      queryClient.invalidateQueries({ queryKey: [AI_AGENTS_QUERY_KEY, variables.agentId] });
    },
  });
}

export function useRemoveContextFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, associationId }: { agentId: string; associationId: string }) =>
      agentApi.removeContextFile(agentId, associationId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [AI_AGENTS_QUERY_KEY, variables.agentId, 'context-files'] });
      queryClient.invalidateQueries({ queryKey: [AI_AGENTS_QUERY_KEY, variables.agentId] });
    },
  });
}

export function useAvailableAvatars() {
  return useQuery({
    queryKey: [AVATARS_QUERY_KEY],
    queryFn: () => agentApi.listAvatars(),
    staleTime: 5 * 60 * 1000, // 5 minutes - avatars config rarely changes
  });
}

// ============================================================================
// PERSISTENT CHAT HOOKS
// ============================================================================

export function useAgentChats(agentId: string, params?: ListChatsParams) {
  return useQuery({
    queryKey: [AGENT_CHATS_QUERY_KEY, agentId, params],
    queryFn: () => agentApi.listAgentChats(agentId, params),
    enabled: !!agentId,
  });
}

export function useAgentChat(agentId: string, chatId: string | null, options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: [AGENT_CHATS_QUERY_KEY, agentId, chatId],
    queryFn: () => agentApi.getAgentChat(agentId, chatId!),
    enabled: !!agentId && !!chatId,
    refetchInterval: options?.refetchInterval,
  });
}

export function useCreateAgentChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId: string) => agentApi.createAgentChat(agentId),
    onSuccess: (_, agentId) => {
      queryClient.invalidateQueries({ queryKey: [AGENT_CHATS_QUERY_KEY, agentId] });
    },
  });
}

export function useSendMessageToChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, chatId, message }: { agentId: string; chatId: string; message: string }) =>
      agentApi.sendMessageToChat(agentId, chatId, message),
    onSuccess: (_, variables) => {
      // Invalidate the specific chat and the chat list
      queryClient.invalidateQueries({ queryKey: [AGENT_CHATS_QUERY_KEY, variables.agentId, variables.chatId] });
      queryClient.invalidateQueries({ queryKey: [AGENT_CHATS_QUERY_KEY, variables.agentId] });
    },
  });
}

export function useEndAgentChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, chatId }: { agentId: string; chatId: string }) =>
      agentApi.endAgentChat(agentId, chatId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [AGENT_CHATS_QUERY_KEY, variables.agentId] });
    },
  });
}

// ============================================================================
// SYSTEM STATUS
// ============================================================================

export function useAgentStatus() {
  return useQuery({
    queryKey: [AGENT_STATUS_QUERY_KEY],
    queryFn: () => agentApi.getStatus(),
    staleTime: 5 * 60 * 1000, // 5 minutes - status rarely changes
  });
}

// ============================================================================
// PROMPT CONFIGURATION
// ============================================================================

export function useAgentPromptConfig() {
  return useQuery({
    queryKey: [AGENT_PROMPT_CONFIG_QUERY_KEY],
    queryFn: () => agentApi.getPromptConfig(),
    staleTime: 10 * 60 * 1000, // 10 minutes - prompt config rarely changes
  });
}
